import { type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import balloon from '@/assets/imgs/home/banner/balloon.svg';
import bird from '@/assets/imgs/home/banner/bird.svg';
import cloud01 from '@/assets/imgs/home/banner/cloud-01.svg';
import cloud02 from '@/assets/imgs/home/banner/cloud-02.svg';
import cloud03 from '@/assets/imgs/home/banner/cloud-03.svg';
import cloud04 from '@/assets/imgs/home/banner/cloud-04.svg';
import cloud05 from '@/assets/imgs/home/banner/cloud-05.svg';
import divider from '@/assets/imgs/home/banner/divider.svg';
import earthBase from '@/assets/imgs/home/banner/earth-base.svg';
import earthFill from '@/assets/imgs/home/banner/earth-fill.svg';
import family from '@/assets/imgs/home/banner/family.svg';
import orbit01 from '@/assets/imgs/home/banner/orbit-01.svg';
import orbit02 from '@/assets/imgs/home/banner/orbit-02.svg';
import orbit03 from '@/assets/imgs/home/banner/orbit-03.svg';
import photoSprite from '@/assets/imgs/home/banner/photo-sprite.png';
import pinBottom from '@/assets/imgs/home/banner/pin-bottom.svg';
import pinLeft from '@/assets/imgs/home/banner/pin-left.svg';
import pinTop from '@/assets/imgs/home/banner/pin-top.svg';
import planeBlue from '@/assets/imgs/home/banner/plane-blue.svg';
import planeOrange from '@/assets/imgs/home/banner/plane-orange.svg';
import travelerCenter from '@/assets/imgs/home/banner/traveler-center.svg';
import travelerRight from '@/assets/imgs/home/banner/traveler-right.svg';
import travelerRightDetail from '@/assets/imgs/home/banner/traveler-right-detail.svg';
import travelerTop from '@/assets/imgs/home/banner/traveler-top.svg';

type MotionDivProps = ComponentProps<typeof motion.div>;

type PhotoConfig = {
  key: string;
  backgroundPosition: string;
  className: string;
  initial: MotionDivProps['initial'];
  animate: MotionDivProps['animate'];
  transition: MotionDivProps['transition'];
};

const photoBaseTimelineDuration = 15;
const photoMotionSlowdown = 1.8;
const photoHoldDuration = 5;
const lastPhotoMotionStart = 0.0565 * photoBaseTimelineDuration;
const lastPhotoMotionEnd = 0.11 * photoBaseTimelineDuration;
const photoMotionCompleteAt = lastPhotoMotionStart
  + (lastPhotoMotionEnd - lastPhotoMotionStart) * photoMotionSlowdown;
const loop = { duration: photoMotionCompleteAt + photoHoldDuration, repeat: Infinity } as const;
const softEase = [0.5, 0, 0.5, 1] as const;

const slowPhotoMotionTimes = (times: number[]) => {
  if (times.length < 3) return times;

  const startIndex = times.length === 3 ? 0 : 1;
  const endIndex = times.length - 2;
  const start = times[startIndex] * photoBaseTimelineDuration;

  return times.map((time, index) => {
    if (index > endIndex) return 1;

    const timeInSeconds = time * photoBaseTimelineDuration;
    const adjustedTime = index <= startIndex
      ? timeInSeconds
      : start + (timeInSeconds - start) * photoMotionSlowdown;

    return adjustedTime / loop.duration;
  });
};

const completeTransition = (
  transition: NonNullable<MotionDivProps['transition']>,
): NonNullable<MotionDivProps['transition']> => {
  const completed = { ...transition } as Record<string, unknown>;

  for (const property of ['opacity', 'scale', 'x', 'y']) {
    const track = completed[property];

    if (track && typeof track === 'object' && !Array.isArray(track)) {
      const propertyTransition = track as Record<string, unknown>;
      const times = propertyTransition.times;

      completed[property] = {
        ...loop,
        ...propertyTransition,
        ...(Array.isArray(times) ? { times: slowPhotoMotionTimes(times) } : {}),
      };
    }
  }

  return completed as NonNullable<MotionDivProps['transition']>;
};

const photos: PhotoConfig[] = [
  {
    key: 'photo-01',
    backgroundPosition: '-85.5rem -26.1875rem',
    className: 'left-[393px] top-[173px] h-[82.629px] w-[83px]',
    initial: { opacity: 0, scale: 0.4, x: -143.6, y: 75.474 },
    animate: {
      opacity: [0, 0, 0.4, 0.7908, 0.7908],
      scale: [0.4, 0.4, 0.79, 0.79],
      x: [-143.6, -143.6, -15.415, -15.415],
      y: [75.474, 75.474, 8.102, 8.102],
    },
    transition: {
      ...loop,
      opacity: { times: [0, 0.042, 0.0421, 0.0912, 1], ease: ['linear', softEase, 'easeIn', 'linear'] },
      scale: { times: [0, 0.0421, 0.0913, 1], ease: ['linear', 'easeIn', 'linear'] },
      x: { times: [0, 0.0421, 0.0912, 1], ease: ['linear', 'easeInOut', 'linear'] },
      y: { times: [0, 0.0421, 0.0912, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-02',
    backgroundPosition: '-66.75rem -26.5rem',
    className: 'left-[93px] top-[178px] h-[114.763px] w-[115px]',
    initial: { opacity: 0.4, scale: 0.4, x: 140, y: 55.047 },
    animate: {
      opacity: [0.4, 0.4, 0.99977, 0.99977],
      scale: [0.4, 0.4, 0.993, 0.993],
      x: [140, 140, 0.006, 0.006],
      y: [55.047, 55.047, 0.002, 0.002],
    },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0316, 0.0803, 1], ease: ['linear', softEase, 'linear'] },
      scale: { times: [0, 0.0316, 0.0803, 1], ease: ['linear', 'easeIn', 'linear'] },
      x: { times: [0, 0.0316, 0.0802, 1], ease: ['linear', 'easeInOut', 'linear'] },
      y: { times: [0, 0.0316, 0.0802, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-03',
    backgroundPosition: '-76.625rem -31.625rem',
    className: 'left-[251px] top-[260px] h-[114.763px] w-[115px]',
    initial: { opacity: 0.4, scale: 0.4, x: -17, y: -27 },
    animate: {
      opacity: [0.4, 0.4, 0, 1, 1],
      scale: [0.4, 0.4, 1, 1],
      x: [-17, -17, 0, 0],
      y: [-27, -27, 0, 0],
    },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0172, 0.0173, 0.0615, 1], ease: ['linear', softEase, 'easeIn', 'linear'] },
      scale: { times: [0, 0.0173, 0.0616, 1], ease: ['linear', 'easeIn', 'linear'] },
      x: { times: [0, 0.0173, 0.0615, 1], ease: ['linear', 'easeInOut', 'linear'] },
      y: { times: [0, 0.0173, 0.0615, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-04',
    backgroundPosition: '-79.125rem -43.6875rem',
    className: 'left-[291px] top-[453px] h-[72.53px] w-[73px]',
    initial: { opacity: 0.4, scale: 0.4, x: -36.9, y: -199.759 },
    animate: {
      opacity: [0.4, 0.4, 0, 1, 1],
      scale: [0.4, 0.4, 1, 1],
      x: [-36.9, -36.9, 0, 0],
      y: [-199.759, -199.759, 0, 0],
    },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0275, 0.0276, 0.0711, 1], ease: ['linear', softEase, 'easeIn', 'linear'] },
      scale: { times: [0, 0.0276, 0.0712, 1], ease: ['linear', 'easeIn', 'linear'] },
      x: { times: [0, 0.0276, 0.0711, 1], ease: ['linear', 'easeInOut', 'linear'] },
      y: { times: [0, 0.0276, 0.0711, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-05',
    backgroundPosition: '-61.9375rem -36.5625rem',
    className: 'left-[16px] top-[339px] h-[92.728px] w-[93px]',
    initial: { opacity: 0.4, scale: 0.4, x: 228.1, y: -95.818 },
    animate: { opacity: [0.4, 1, 1], scale: [0.4, 1, 1], x: [228.1, 0, 0], y: [-95.818, 0, 0] },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0498, 1], ease: ['easeIn', 'linear'] },
      scale: { times: [0, 0.0499, 1], ease: ['easeIn', 'linear'] },
      x: { times: [0, 0.0498, 1], ease: ['easeInOut', 'linear'] },
      y: { times: [0, 0.0498, 1], ease: ['easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-06',
    backgroundPosition: '-76.625rem -13.8125rem',
    className: 'left-[251px] top-[-25px] h-[123.944px] w-[125px]',
    initial: { opacity: 0.4, scale: 0.4, x: -22.5, y: 252.817 },
    animate: {
      opacity: [0.4, 0.4, 1, 1],
      scale: [0.4, 0.4, 1, 1],
      x: [-22.5, -22.5, 0, 0],
      y: [252.817, 252.817, 0, 0],
    },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0111, 0.0561, 1], ease: ['linear', 'easeIn', 'linear'] },
      scale: { times: [0, 0.0111, 0.0561, 1], ease: ['linear', 'easeIn', 'linear'] },
      x: { times: [0, 0.0111, 0.0561, 1], ease: ['linear', 'easeInOut', 'linear'] },
      y: { times: [0, 0.0111, 0.0561, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-07',
    backgroundPosition: '-94.6875rem -23.3125rem',
    className: 'left-[540px] top-[127px] h-[114.763px] w-[115px]',
    initial: { opacity: 0.4, scale: 0.4, x: -307.5, y: 105.571 },
    animate: {
      opacity: [0.4, 0.4, 0, 1, 1],
      scale: [0.4, 0.4, 1, 1],
      x: [-307.5, -307.5, 0, 0],
      y: [105.571, 105.571, 0, 0],
    },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0198, 0.0199, 0.0629, 1], ease: ['linear', softEase, 'easeIn', 'linear'] },
      scale: { times: [0, 0.0199, 0.0629, 1], ease: ['linear', 'easeIn', 'linear'] },
      x: { times: [0, 0.0199, 0.0629, 1], ease: ['linear', 'easeInOut', 'linear'] },
      y: { times: [0, 0.0199, 0.0629, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-08',
    backgroundPosition: '-89.4375rem -38.25rem',
    className: 'left-[456px] top-[366px] size-[80px]',
    initial: { opacity: 0.4, scale: 0 },
    animate: { opacity: [0.4, 0.4, 0.75202, 0.75202], scale: [0, 0, 0.575, 0.575] },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0516, 0.1038, 1], ease: ['linear', softEase, 'linear'] },
      scale: { times: [0, 0.0516, 0.1038, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-09',
    backgroundPosition: '-88.125rem -16.625rem',
    className: 'left-[435px] top-[20px] h-[95.483px] w-[95px]',
    initial: { opacity: 0.4, scale: 0 },
    animate: { opacity: [0.4, 0.4, 0.98226, 0.98226], scale: [0, 0, 0.961, 0.961] },
    transition: {
      ...loop,
      opacity: { times: [0, 0.039, 0.0866, 1], ease: ['linear', softEase, 'linear'] },
      scale: { times: [0, 0.039, 0.0866, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
  {
    key: 'photo-10',
    backgroundPosition: '-61.3125rem -18rem',
    className: 'left-[6px] top-[42px] size-[97px]',
    initial: { opacity: 0.4, scale: 0 },
    animate: { opacity: [0.4, 0.4, 0.62743, 0.62743], scale: [0, 0, 0.395, 0.395] },
    transition: {
      ...loop,
      opacity: { times: [0, 0.0565, 0.11, 1], ease: ['linear', softEase, 'linear'] },
      scale: { times: [0, 0.0565, 0.11, 1], ease: ['linear', 'easeInOut', 'linear'] },
    },
  },
];

const Banner = () => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const taglineSize = i18n.resolvedLanguage === 'en-US' ? 'text-[36px]' : 'text-[45px]';

  return (
    <section
      className="relative h-[100svh] min-h-[760px] min-w-[1480px] overflow-hidden bg-[linear-gradient(167.212deg,#e5f4ff_14.07%,#d0eaff_85.16%)]"
      aria-labelledby="home-banner-title"
      data-home-banner
    >
      <div className="absolute left-1/2 top-1/2 h-[1050px] w-[1920px] -translate-x-1/2 -translate-y-1/2 overflow-hidden">
        <div className="absolute left-[220px] top-[225px] z-20 w-[550px]">
          <h1
            id="home-banner-title"
            className="font-expose text-[115px] font-bold leading-[0.9734] tracking-[-1.156px] text-[#00a0e9]"
          >
            <span className="block">Travel Far</span>
            <span className="block">Feel More</span>
          </h1>

          <p className={`mt-[35px] font-douyin ${taglineSize} font-bold leading-[1.2] text-[#515151]`}>
            <span className="block">{t('home.banner.taglineLine1')}</span>
            <span className="block">{t('home.banner.taglineLine2')}</span>
          </p>

          <img src={divider} alt="" className="mt-[27px] h-[4px] w-[116px]" />

          <p className="mt-[36px] font-douyin text-[28px] font-normal leading-[1.4] text-[#355f72]">
            <span className="block">{t('home.banner.descriptionLine1')}</span>
            <span className="block">{t('home.banner.descriptionLine2')}</span>
          </p>

          {/* <Link
            to="/about"
            className="group mt-[48px] inline-flex h-[60px] w-[240px] items-center justify-center overflow-hidden rounded-[85px] border-2 border-[#00a0e9] bg-[#00a0e9] font-douyin text-[28px] leading-none text-white transition-[background-color,color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-white hover:text-[#00a0e9] motion-reduce:duration-0"
          >
            <span
              className="flex items-center gap-[10px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-[19px] motion-reduce:duration-0"
            >
              View More
              <span
                className="flex size-[28px] items-center justify-center opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
                aria-hidden="true"
              >
                <img src={arrow} alt="" className="h-[11px] w-[20px] -rotate-90" />
              </span>
            </span>
          </Link> */}
        </div>

        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <img src={cloud04} alt="" className="absolute left-[618px] top-[136px] h-[47px] w-[115px]" />
          <img src={bird} alt="" className="absolute left-[846px] top-[185px] h-[18px] w-[59px] -scale-x-100" />
          <img src={cloud03} alt="" className="absolute left-[806px] top-[327px] h-[63px] w-[154px]" />
          <img src={cloud05} alt="" className="absolute left-[555px] top-[581px] h-[47px] w-[115px]" />
          <img src={cloud01} alt="" className="absolute left-[1660px] top-[157px] h-[52px] w-[127px]" />
          <img src={cloud02} alt="" className="absolute left-[1532px] top-[494px] h-[56px] w-[137px]" />
          <img src={balloon} alt="" className="absolute left-[1569px] top-[121px] h-[89px] w-[63px]" />

          <img src={orbit02} alt="" className="absolute left-[1177px] top-[105px] h-[843px] w-[250px] rotate-[34.5deg]" />
          <img src={orbit03} alt="" className="absolute left-[1077px] top-[105px] h-[843px] w-[250px] -rotate-[39.83deg]" />
          <img src={orbit01} alt="" className="absolute left-[946px] top-[217px] size-[645px]" />

          <img src={earthBase} alt="" className="absolute left-[975px] top-[246px] size-[584px]" />
          <img src={earthFill} alt="" className="absolute left-[975px] top-[247px] h-[538.731px] w-[582.038px]" />

          <div className="absolute left-[975px] top-[246px] z-10 size-[584px]">
            {photos.map((photo) => (
              <motion.div
                key={photo.key}
                className={`absolute origin-center overflow-hidden rounded-[8px] bg-no-repeat shadow-[0_3px_8px_rgba(0,0,0,0.28)] will-change-transform ${photo.className}`}
                style={{
                  backgroundImage: `url(${photoSprite})`,
                  backgroundPosition: photo.backgroundPosition,
                  backgroundSize: '120rem 65.625rem',
                }}
                initial={reduceMotion ? false : photo.initial}
                animate={reduceMotion ? undefined : photo.animate}
                transition={
                  reduceMotion || !photo.transition
                    ? undefined
                    : completeTransition(photo.transition)
                }
              />
            ))}
          </div>

          <img src={pinLeft} alt="" className="absolute left-[959px] top-[489px] z-20 size-[68px]" />
          <img src={pinTop} alt="" className="absolute left-[1157px] top-[222px] z-20 size-[57px]" />
          <img src={pinBottom} alt="" className="absolute left-[1370.5px] top-[692px] z-20 size-[87px]" />

          <div className="absolute left-[945px] top-[407px] z-20 flex size-[39px] rotate-[19deg] items-center justify-center">
            <img src={planeOrange} alt="" className="w-[31px]" />
          </div>
          <img src={planeBlue} alt="" className="absolute left-[1568px] top-[231px] h-[106px] w-[138px]" />

          <img src={travelerCenter} alt="" className="absolute left-[789px] top-[313px] z-20 h-[192px] w-[98px]" />
          <img src={family} alt="" className="absolute left-[705px] top-[628px] z-20 h-[253px] w-[273px]" />
          <img src={travelerTop} alt="" className="absolute left-[1650px] top-[198px] z-20 h-[227px] w-[139px] -scale-x-100" />
          <div className="absolute left-[1615px] top-[581px] z-20 h-[320px] w-[198px] -scale-x-100">
            <img src={travelerRight} alt="" className="absolute left-0 top-0 h-[320px] w-[171.359px]" />
            <img src={travelerRightDetail} alt="" className="absolute left-[130.6px] top-[172.2px] h-[147.79px] w-[66.493px]" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Banner;
